import React, { useMemo, useState } from 'react';
import {
  PlusIcon,
  CheckCircleIcon,
  XIcon,
  RefreshCwIcon,
  Trash2Icon,
  PencilIcon,
  BanknoteIcon,
  Calendar,
  Clock,
  CreditCard,
  Check,
  FileTextIcon,
  ChevronDown,
  ChevronUp,
  Pill,
  BriefcaseIcon,
} from 'lucide-react';
import { Toast } from '../Toast';
import {
  useClientPayments,
  useInvalidatePayments,
  PaymentRecord,
} from '../../hooks/usePayments';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';
import { useMutation } from '@tanstack/react-query';
import ActionCard from '../common/ActionCard';
import EntityStatusBadge from '../common/EntityStatusBadge';
import { InvoiceFormModal } from '../invoices/InvoiceFormModal';
import { useHasInvoiceSettings } from '../../hooks/useInvoiceSettings';
import { useInvoices } from '../../hooks/useInvoices';
import { LineItemBuilder } from '../payments/LineItemBuilder';
import type { Payment } from '../../hooks/useAllPayments';
import type {
  PaymentInputMode,
  PaymentLineItemFormData,
  PaymentLineItemWithCalculations,
  DiscountType,
} from '../../types/services';
import {
  calculateLineItemNetAmount,
  calculatePaymentTotals,
  formatCurrency as formatCurrencyUtil,
} from '../../types/services';
import { useNavigate } from 'react-router-dom';

interface PaymentsTabProps {
  clientId: string;
  clientName?: string;
  clientPhone?: string;
  onNavigateToTab?: (tab: string) => void;
}

type PaymentFilter = 'all' | 'collected' | 'outstanding';

interface PaymentFilterStats {
  all: number;
  collected: number;
  outstanding: number;
}

type PaymentFormState = {
  inputMode: PaymentInputMode;
  amount: string;
  paymentMethod: string;
  status: 'pending' | 'paid' | 'expired';
  dueDate: string;
  paidDate: string;
  notes: string;
  // Detailed mode fields
  lineItems: PaymentLineItemFormData[];
  overallDiscountType: DiscountType | null | undefined;
  overallDiscountValue: number | undefined;
  overallDiscountReason: string | null | undefined;
  taxEnabled: boolean;
  taxRate: number;
};

function getCurrentLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getEmptyFormState(): PaymentFormState {
  const today = getCurrentLocalDate();
  return {
    inputMode: 'quick',
    amount: '',
    paymentMethod: '',
    status: 'pending',
    dueDate: today,
    paidDate: today,
    notes: '',
    lineItems: [],
    overallDiscountType: null,
    overallDiscountValue: undefined,
    overallDiscountReason: null,
    taxEnabled: false,
    taxRate: 18,
  };
}

function toDateInputValue(value?: string | null) {
  if (!value) return '';
  return value.split('T')[0] ?? '';
}

function combineDateTime(date: string, fallback?: string) {
  if (date) {
    // Use start of day (midnight) in local timezone
    const iso = new Date(`${date}T00:00:00`);
    if (!Number.isNaN(iso.getTime())) {
      return iso.toISOString();
    }
  }
  if (fallback) {
    const fallbackDate = new Date(fallback);
    if (!Number.isNaN(fallbackDate.getTime())) {
      return fallbackDate.toISOString();
    }
  }
  return null;
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })} • ${date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

export function PaymentsTab({
  clientId,
  clientName,
  clientPhone,
  onNavigateToTab,
}: PaymentsTabProps) {
  const { session } = useAuth();
  const navigate = useNavigate();
  const paymentsQuery = useClientPayments(clientId);
  const invalidatePayments = useInvalidatePayments(clientId);
  const { isConfigured: hasInvoiceSettings } = useHasInvoiceSettings();

  // Fetch all invoices to check which payments have invoices
  const { data: invoicesData } = useInvoices();
  const invoices = invoicesData?.invoices || [];

  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] =
    useState<PaymentRecord | null>(null);
  const [formState, setFormState] =
    useState<PaymentFormState>(getEmptyFormState());
  const [formError, setFormError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<PaymentFilter>('all');
  const [expandedPayments, setExpandedPayments] = useState<Set<string>>(new Set());

  // Invoice modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoicePayment, setInvoicePayment] = useState<PaymentRecord | null>(null);

  // Helper to check if payment has invoice
  const getInvoiceForPayment = (paymentId: string) => {
    return invoices.find(inv => inv.payment_id === paymentId);
  };

  const payments = paymentsQuery.data ?? [];
  const nextPayment = useMemo(() => {
    return payments
      .filter((payment) => payment.status === 'pending')
      .sort(
        (a, b) =>
          new Date(a.due_at).getTime() - new Date(b.due_at).getTime(),
      )[0];
  }, [payments]);

  const totals = useMemo(() => {
    return payments.reduce(
      (acc, payment) => {
        if (payment.status === 'paid') {
          acc.paid += payment.amount;
        } else {
          acc.outstanding += payment.amount;
        }
        return acc;
      },
      { paid: 0, outstanding: 0 },
    );
  }, [payments]);

  const filterStats = useMemo(() => {
    return payments.reduce(
      (acc, payment) => {
        acc.all += 1;
        if (payment.status === 'paid') {
          acc.collected += 1;
        } else {
          // pending or expired = outstanding
          acc.outstanding += 1;
        }
        return acc;
      },
      { all: 0, collected: 0, outstanding: 0 },
    );
  }, [payments]);

  const filteredPayments = useMemo(() => {
    if (activeFilter === 'all') return payments;
    if (activeFilter === 'collected') {
      return payments.filter(p => p.status === 'paid');
    }
    // outstanding: pending or expired
    return payments.filter(p => p.status === 'pending' || p.status === 'expired');
  }, [payments, activeFilter]);

  const paymentMutation = useMutation({
    mutationFn: async ({
      paymentId,
      payload,
    }: {
      paymentId?: string | null;
      payload: Record<string, unknown>;
    }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      const method = paymentId ? 'PATCH' : 'POST';
      const url = paymentId
        ? `/api/clients/${clientId}/payments/${paymentId}`
        : `/api/clients/${clientId}/payments`;
      return apiRequest<PaymentRecord>(
        url,
        {
          method,
          body: JSON.stringify(payload),
        },
        session.access_token,
      );
    },
    onSuccess: (data, variables) => {
      setShowForm(false);
      setFormState(getEmptyFormState());
      setEditingPayment(null);
      setFormError(null);
      setToastMessage(
        variables.paymentId ? 'Payment updated' : 'Payment saved',
      );
      invalidatePayments();
    },
    onError: (error) => {
      setFormError(
        error instanceof Error ? error.message : 'Failed to save payment',
      );
    },
  });

  const deletePaymentMutation = useMutation({
    mutationFn: async ({ paymentId }: { paymentId: string }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      return apiRequest<PaymentRecord>(
        `/api/clients/${clientId}/payments/${paymentId}`,
        { method: 'DELETE' },
        session.access_token,
      );
    },
    onSuccess: () => {
      setToastMessage('Payment deleted');
      invalidatePayments();
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({
      paymentId,
      updates,
    }: {
      paymentId: string;
      updates: Record<string, unknown>;
    }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      return apiRequest<PaymentRecord>(
        `/api/clients/${clientId}/payments/${paymentId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(updates),
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      setToastMessage('Payment updated');
      invalidatePayments();
    },
  });

  const handleOpenForm = (payment?: PaymentRecord) => {
    if (payment) {
      setEditingPayment(payment);
      // Determine input mode based on payment data
      const hasLineItems = payment.line_items && payment.line_items.length > 0;
      const isDetailedMode = (payment as any).input_mode === 'detailed' || hasLineItems;

      setFormState({
        inputMode: isDetailedMode ? 'detailed' : 'quick',
        amount: payment.amount.toString(),
        paymentMethod: payment.payment_method ?? 'UPI',
        status: payment.status,
        dueDate: toDateInputValue(payment.due_at),
        paidDate: toDateInputValue(payment.paid_at ?? payment.due_at),
        notes: payment.notes ?? '',
        lineItems: hasLineItems
          ? payment.line_items!.map((item) => ({
              id: item.id,
              item_type: item.item_type,
              service_id: (item as any).service_id,
              product_id: (item as any).product_id,
              description: item.description,
              quantity: item.quantity,
              unit_price: item.unit_price,
              discount_type: (item as any).discount_type,
              discount_value: (item as any).discount_value,
              discount_reason: (item as any).discount_reason,
            }))
          : [],
        overallDiscountType: (payment as any).overall_discount_type,
        overallDiscountValue: (payment as any).overall_discount_value,
        overallDiscountReason: (payment as any).overall_discount_reason,
        taxEnabled: (payment as any).tax_enabled ?? false,
        taxRate: (payment as any).tax_rate ?? 18,
      });
    } else {
      setEditingPayment(null);
      setFormState(getEmptyFormState());
    }
    setFormError(null);
    setShowForm(true);
  };

  const handleGenerateInvoice = (payment: PaymentRecord) => {
    setInvoicePayment(payment);
    setShowInvoiceModal(true);
  };

  const handleInvoiceSuccess = (message: string) => {
    setToastMessage(message);
    setShowInvoiceModal(false);
    setInvoicePayment(null);
  };

  const handleInvoiceError = (message: string) => {
    setToastMessage(message);
  };

  const handleSavePayment = async () => {
    if (!formState.dueDate) {
      setFormError('Due date is required');
      return;
    }

    const dueAt = combineDateTime(formState.dueDate);
    if (!dueAt) {
      setFormError('Enter a valid due date');
      return;
    }

    let paidAt: string | null = null;
    if (formState.status === 'paid') {
      paidAt =
        combineDateTime(
          formState.paidDate || formState.dueDate,
          dueAt,
        ) ?? new Date().toISOString();
    } else if (formState.paidDate) {
      paidAt = combineDateTime(
        formState.paidDate,
        dueAt,
      );
    }

    let finalAmount: number;
    let payload: Record<string, unknown>;

    if (formState.inputMode === 'quick') {
      // Quick mode - simple amount
      if (!formState.amount.trim()) {
        setFormError('Amount is required');
        return;
      }
      const amountValue = Number(formState.amount);
      if (Number.isNaN(amountValue) || amountValue <= 0) {
        setFormError('Enter a valid amount');
        return;
      }
      finalAmount = amountValue;

      payload = {
        input_mode: 'quick',
        amount: finalAmount,
        payment_method: formState.paymentMethod.trim() || null,
        status: formState.status,
        due_at: dueAt,
        paid_at: paidAt,
        notes: formState.notes.trim() || null,
      };
    } else {
      // Detailed mode - calculate from line items
      if (formState.lineItems.length === 0) {
        setFormError('Add at least one item');
        return;
      }

      // Validate line items
      for (const item of formState.lineItems) {
        if (!item.description.trim()) {
          setFormError('All items must have a description');
          return;
        }
        if (item.unit_price <= 0) {
          setFormError('All items must have a valid price');
          return;
        }
      }

      // Calculate line items with calculations
      const itemsWithCalc: PaymentLineItemWithCalculations[] = formState.lineItems.map((item) => {
        const { grossAmount, discountAmount, netAmount } = calculateLineItemNetAmount(
          item.quantity,
          item.unit_price,
          item.discount_type,
          item.discount_value
        );
        return { ...item, gross_amount: grossAmount, discount_amount: discountAmount, net_amount: netAmount };
      });

      const totals = calculatePaymentTotals(
        itemsWithCalc,
        formState.overallDiscountType,
        formState.overallDiscountValue,
        formState.taxEnabled,
        formState.taxRate
      );

      finalAmount = totals.grandTotal;

      payload = {
        input_mode: 'detailed',
        amount: finalAmount,
        subtotal: totals.subtotal,
        overall_discount_type: formState.overallDiscountType || null,
        overall_discount_value: formState.overallDiscountValue ?? 0,
        overall_discount_amount: totals.overallDiscountAmount,
        overall_discount_reason: formState.overallDiscountReason || null,
        tax_enabled: formState.taxEnabled,
        tax_rate: formState.taxRate,
        tax_amount: totals.taxAmount,
        payment_method: formState.paymentMethod.trim() || null,
        status: formState.status,
        due_at: dueAt,
        paid_at: paidAt,
        notes: formState.notes.trim() || null,
        line_items: formState.lineItems.map((item) => {
          const { grossAmount, discountAmount, netAmount } = calculateLineItemNetAmount(
            item.quantity,
            item.unit_price,
            item.discount_type,
            item.discount_value
          );
          return {
            item_type: item.item_type,
            service_id: item.service_id || null,
            product_id: item.product_id || null,
            description: item.description.trim(),
            quantity: item.quantity,
            unit_price: item.unit_price,
            discount_type: item.discount_type || null,
            discount_value: item.discount_value ?? 0,
            discount_amount: discountAmount,
            discount_reason: item.discount_reason || null,
            net_amount: netAmount,
          };
        }),
      };
    }

    await paymentMutation.mutateAsync({
      paymentId: editingPayment?.id ?? null,
      payload,
    });
  };

  const handleMarkPaid = async (payment: PaymentRecord) => {
    await statusMutation.mutateAsync({
      paymentId: payment.id,
      updates: {
        status: 'paid',
        paid_at: new Date().toISOString(),
      },
    });
  };

  const handleDeletePayment = async (payment: PaymentRecord) => {
    await deletePaymentMutation.mutateAsync({ paymentId: payment.id });
  };

  const togglePaymentExpansion = (paymentId: string) => {
    setExpandedPayments((prev) => {
      const next = new Set(prev);
      if (next.has(paymentId)) {
        next.delete(paymentId);
      } else {
        next.add(paymentId);
      }
      return next;
    });
  };

  const paymentMethods = [
    'UPI',
    'Cash',
    'Debit Card',
    'Credit Card',
    'Bank Transfer',
  ];

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Collected</p>
          <p className="text-2xl font-semibold text-[#5B8E87]">
            {formatCurrency(totals.paid, 'INR')}
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Outstanding</p>
          <p
            className={`text-2xl font-semibold ${
              totals.outstanding > 0 ? 'text-red-500' : 'text-gray-500'
            }`}
          >
            {formatCurrency(totals.outstanding, 'INR')}
          </p>
        </div>
      </div>

      {/* Filter Chips */}
      <div className="flex flex-wrap gap-2">
        {(['all', 'collected', 'outstanding'] as PaymentFilter[]).map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            disabled={paymentsQuery.isLoading}
            aria-pressed={activeFilter === filter}
            aria-label={`Filter payments: ${filter === 'all' ? 'All' : filter === 'collected' ? 'Collected' : 'Outstanding'}`}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeFilter === filter
                ? 'bg-[#5B8E87] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } ${paymentsQuery.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {filter === 'all'
              ? `All (${filterStats.all})`
              : filter === 'collected'
              ? `Collected (${filterStats.collected})`
              : `Outstanding (${filterStats.outstanding})`}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="p-5 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-base text-[#212121]">
              Payment history
            </h3>
            <p className="text-xs text-gray-500">
              {payments.length} records
            </p>
          </div>
        </div>

        {paymentsQuery.isLoading ? (
          <div className="p-6 text-center text-gray-500">Loading…</div>
        ) : paymentsQuery.isError ? (
          <div className="p-6 text-center text-red-500">
            Unable to load payments.
          </div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BanknoteIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-[#212121] mb-2">
              No payments yet
            </h3>
            <p className="text-gray-600 mb-4">
              Track payments and outstanding dues for this client.
            </p>
            <button
              onClick={() => handleOpenForm()}
              className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg font-medium"
            >
              Add payment
            </button>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="p-6 text-center text-gray-600">
            {activeFilter === 'all'
              ? 'No payments recorded yet.'
              : `No ${activeFilter} payments.`}
          </div>
        ) : (
          <div className="p-5 space-y-3">
            {filteredPayments.map((payment) => {
              const isOverdue =
                payment.status === 'pending' &&
                new Date(payment.due_at) < new Date();
              const isPaid = payment.status === 'paid';
              const isPending = payment.status === 'pending';
              const isNextPayment = nextPayment?.id === payment.id;

              // Format date and time
              const dueDate = new Date(payment.due_at);
              const formattedDate = dueDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });
              const formattedTime = dueDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              });

              // Determine icon colors
              const iconBgColor = isOverdue
                ? 'bg-red-100'
                : isPaid
                ? 'bg-[#5B8E87]/10'
                : 'bg-[#C9A870]/10';
              const iconColor = isOverdue
                ? 'text-red-600'
                : isPaid
                ? 'text-[#5B8E87]'
                : 'text-[#C9A870]';

              // Card state for border
              const cardState = isPaid
                ? 'completed'
                : payment.status === 'expired'
                ? 'cancelled'
                : 'default';

              // Build metadata array
              const metadata = [
                {
                  icon: <Calendar className="w-3.5 h-3.5" />,
                  text: formattedDate,
                },
                {
                  icon: <Clock className="w-3.5 h-3.5" />,
                  text: formattedTime,
                },
                {
                  icon: <CreditCard className="w-3.5 h-3.5" />,
                  text: payment.payment_method ?? 'UPI',
                },
              ];

              // Title content
              const titleContent = (
                <div>
                  <div className="text-xl font-bold text-[#212121]">
                    {formatCurrency(payment.amount, payment.currency)}
                  </div>
                  {payment.notes && (
                    <div className="text-sm text-gray-500 italic mt-1">
                      {payment.notes}
                    </div>
                  )}
                </div>
              );

              // Status badge
              const statusBadge = (
                <EntityStatusBadge
                  type="payment"
                  status={
                    isPaid ? 'Paid' : isOverdue ? 'Overdue' : 'Pending'
                  }
                />
              );

              // Add "Next" badge if this is the next payment
              const additionalBadge = isNextPayment ? (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                  Next
                </span>
              ) : null;

              // Check if payment has line items
              const hasLineItems = payment.line_items && payment.line_items.length > 0;
              const isExpanded = expandedPayments.has(payment.id);

              return (
                <div key={payment.id} className="space-y-2">
                <ActionCard
                  key={payment.id}
                  icon={<BanknoteIcon className="w-5 h-5" />}
                  iconBgColor={iconBgColor}
                  iconColor={iconColor}
                  title={titleContent}
                  metadata={metadata}
                  showClientInfo={false}
                  statusBadge={
                    <>
                      {statusBadge}
                      {additionalBadge}
                    </>
                  }
                  cardState={cardState}
                  primaryAction={
                    isPending
                      ? {
                          label: 'Mark Paid',
                          icon: <Check className="w-4 h-4" />,
                          onClick: () => handleMarkPaid(payment),
                          variant: 'success',
                        }
                      : undefined
                  }
                  menuItems={[
                    ...(isPaid && hasInvoiceSettings
                      ? (() => {
                          const existingInvoice = getInvoiceForPayment(payment.id);
                          return existingInvoice
                            ? [
                                {
                                  label: 'View Invoice',
                                  icon: <FileTextIcon className="w-4 h-4" />,
                                  onClick: () => navigate(`/invoices/${existingInvoice.id}`),
                                },
                              ]
                            : [
                                {
                                  label: 'Generate Invoice',
                                  icon: <FileTextIcon className="w-4 h-4" />,
                                  onClick: () => handleGenerateInvoice(payment),
                                },
                              ];
                        })()
                      : []),
                    {
                      label: 'Edit',
                      icon: <PencilIcon className="w-4 h-4" />,
                      onClick: () => handleOpenForm(payment),
                    },
                    {
                      label: 'Delete',
                      icon: <Trash2Icon className="w-4 h-4" />,
                      onClick: () => handleDeletePayment(payment),
                      variant: 'danger',
                    },
                  ]}
                  actionsLayout="inline"
                />

                {/* Line Items Section */}
                {hasLineItems && (
                  <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => togglePaymentExpansion(payment.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">
                          Items ({payment.line_items?.length})
                        </span>
                        {payment.appointment_id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/appointment/${payment.appointment_id}`);
                            }}
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors flex items-center gap-1"
                          >
                            <Calendar className="w-3 h-3" />
                            Appointment
                          </button>
                        )}
                        {(payment as any).input_mode === 'detailed' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-600">
                            Itemized
                          </span>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-200">
                        {/* Line Items */}
                        <div className="divide-y divide-gray-200">
                          {payment.line_items?.map((item) => {
                            const lineItem = item as any;
                            const hasDiscount = lineItem.discount_amount != null && lineItem.discount_amount > 0;
                            const displayAmount = lineItem.net_amount ?? lineItem.gross_amount ?? (item.quantity * item.unit_price);

                            return (
                              <div
                                key={item.id}
                                className="px-4 py-3"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-2 flex-1">
                                    <div
                                      className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center ${
                                        item.item_type === 'supplement'
                                          ? 'bg-[#5B8E87]/10'
                                          : item.item_type === 'service'
                                          ? 'bg-[#C9A870]/10'
                                          : 'bg-gray-100'
                                      }`}
                                    >
                                      {item.item_type === 'supplement' ? (
                                        <Pill className="w-3.5 h-3.5 text-[#5B8E87]" />
                                      ) : item.item_type === 'service' ? (
                                        <BriefcaseIcon className="w-3.5 h-3.5 text-[#C9A870]" />
                                      ) : (
                                        <span className="text-xs font-medium text-gray-600">
                                          #
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900">
                                        {item.description}
                                      </p>
                                      {item.product && (
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          {[
                                            item.product.brand,
                                            item.product.form,
                                            item.product.dosage,
                                          ]
                                            .filter(Boolean)
                                            .join(' • ')}
                                        </p>
                                      )}
                                      <p className="text-xs text-gray-500 mt-1">
                                        {item.quantity} × {formatCurrency(Number(item.unit_price) || 0, payment.currency)}
                                        {hasDiscount && (
                                          <span className="text-red-500 ml-2">
                                            -{formatCurrency(lineItem.discount_amount, payment.currency)}
                                            {lineItem.discount_reason && (
                                              <span className="text-gray-400"> ({lineItem.discount_reason})</span>
                                            )}
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-semibold text-gray-900">
                                      {formatCurrency(displayAmount, payment.currency)}
                                    </p>
                                    <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700 capitalize">
                                      {item.item_type}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Payment Summary for detailed mode */}
                        {(payment as any).input_mode === 'detailed' && (
                          <div className="border-t border-gray-200 px-4 py-3 bg-white space-y-1.5">
                            {(payment as any).subtotal && (
                              <div className="flex justify-between text-xs text-gray-500">
                                <span>Subtotal</span>
                                <span>{formatCurrency((payment as any).subtotal, payment.currency)}</span>
                              </div>
                            )}
                            {(payment as any).overall_discount_amount > 0 && (
                              <div className="flex justify-between text-xs text-red-500">
                                <span>
                                  Discount
                                  {(payment as any).overall_discount_reason && (
                                    <span className="text-gray-400 ml-1">
                                      ({(payment as any).overall_discount_reason})
                                    </span>
                                  )}
                                </span>
                                <span>-{formatCurrency((payment as any).overall_discount_amount, payment.currency)}</span>
                              </div>
                            )}
                            {(payment as any).tax_enabled && (payment as any).tax_amount > 0 && (
                              <div className="flex justify-between text-xs text-gray-500">
                                <span>Tax ({(payment as any).tax_rate}%)</span>
                                <span>+{formatCurrency((payment as any).tax_amount, payment.currency)}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-sm font-semibold text-gray-900 pt-1 border-t border-gray-200">
                              <span>Total</span>
                              <span>{formatCurrency(payment.amount, payment.currency)}</span>
                            </div>
                          </div>
                        )}

                        {/* Link to Appointment if payment is linked */}
                        {payment.appointment_id && (
                          <div className="border-t border-gray-200 px-4 py-2 bg-blue-50">
                            <button
                              onClick={() => navigate(`/appointment/${payment.appointment_id}`)}
                              className="w-full text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors flex items-center justify-center gap-1"
                            >
                              <Calendar className="w-4 h-4" />
                              View Appointment →
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      <button
        onClick={() => handleOpenForm()}
        className="fixed bottom-24 right-6 bg-[#C9A870] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#B89660] transition-all active:scale-95 z-20"
      >
        <PlusIcon className="w-6 h-6" />
      </button>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-[#212121]">
                {editingPayment ? 'Edit payment' : 'Add payment'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setFormState(getEmptyFormState());
                  setEditingPayment(null);
                  setFormError(null);
                }}
                className="text-gray-500"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {formError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  {formError}
                </div>
              )}

              {/* Mode Toggle */}
              <div className="flex rounded-xl border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() =>
                    setFormState((prev) => ({
                      ...prev,
                      inputMode: 'quick',
                      lineItems: [],
                      overallDiscountType: null,
                      overallDiscountValue: undefined,
                      overallDiscountReason: null,
                      taxEnabled: false,
                    }))
                  }
                  className={`flex-1 py-3 text-sm font-medium transition-colors ${
                    formState.inputMode === 'quick'
                      ? 'bg-[#5B8E87] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Quick
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setFormState((prev) => ({
                      ...prev,
                      inputMode: 'detailed',
                      amount: '',
                    }))
                  }
                  className={`flex-1 py-3 text-sm font-medium transition-colors border-l border-gray-300 ${
                    formState.inputMode === 'detailed'
                      ? 'bg-[#5B8E87] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Detailed
                </button>
              </div>

              {/* Quick Mode - Simple Amount */}
              {formState.inputMode === 'quick' && (
                <div>
                  <label className="block text-sm font-medium text-[#212121] mb-2">
                    Amount (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formState.amount}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                    placeholder="0"
                    min="0"
                    step="1"
                  />
                </div>
              )}

              {/* Detailed Mode - Line Items Builder */}
              {formState.inputMode === 'detailed' && (
                <LineItemBuilder
                  lineItems={formState.lineItems}
                  overallDiscountType={formState.overallDiscountType}
                  overallDiscountValue={formState.overallDiscountValue}
                  overallDiscountReason={formState.overallDiscountReason}
                  taxEnabled={formState.taxEnabled}
                  taxRate={formState.taxRate}
                  onLineItemsChange={(items) =>
                    setFormState((prev) => ({ ...prev, lineItems: items }))
                  }
                  onOverallDiscountChange={(type, value, reason) =>
                    setFormState((prev) => ({
                      ...prev,
                      overallDiscountType: type,
                      overallDiscountValue: value,
                      overallDiscountReason: reason,
                    }))
                  }
                  onTaxEnabledChange={(enabled) =>
                    setFormState((prev) => ({ ...prev, taxEnabled: enabled }))
                  }
                  onTaxRateChange={(rate) =>
                    setFormState((prev) => ({ ...prev, taxRate: rate }))
                  }
                />
              )}

              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Payment method
                </label>
                <select
                  value={formState.paymentMethod}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                >
                  <option value="">Not specified</option>
                  {paymentMethods.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Status
                </label>
                <select
                  value={formState.status}
                  onChange={(e) => {
                    const newStatus = e.target.value as PaymentFormState['status'];
                    setFormState((prev) => {
                      // Auto-fill paid date if changing to 'paid' and it's empty
                      if (newStatus === 'paid' && !prev.paidDate) {
                        return {
                          ...prev,
                          status: newStatus,
                          paidDate: getCurrentLocalDate(),
                        };
                      }
                      return {
                        ...prev,
                        status: newStatus,
                      };
                    });
                  }}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Due Date {formState.status !== 'paid' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="date"
                  value={formState.dueDate}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      dueDate: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                />
              </div>
              {formState.status === 'paid' && (
                <div>
                  <label className="block text-sm font-medium text-[#212121] mb-2">
                    Paid On <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formState.paidDate}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        paidDate: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Notes
                </label>
                <textarea
                  value={formState.notes}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                  placeholder="e.g., Advance for 3-month plan"
                />
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-3 sticky bottom-0">
              <button
                onClick={() => {
                  setShowForm(false);
                  setFormState(getEmptyFormState());
                  setEditingPayment(null);
                  setFormError(null);
                }}
                className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePayment}
                disabled={paymentMutation.isPending}
                className={`flex-1 font-semibold py-3 rounded-xl ${
                  paymentMutation.isPending
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-[#5B8E87] text-white'
                }`}
              >
                {paymentMutation.isPending ? 'Saving...' : 'Save Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && invoicePayment && clientName && clientPhone && (
        <InvoiceFormModal
          isOpen={showInvoiceModal}
          onClose={() => {
            setShowInvoiceModal(false);
            setInvoicePayment(null);
          }}
          onSuccess={handleInvoiceSuccess}
          onError={handleInvoiceError}
          clientId={clientId}
          clientName={clientName}
          clientPhone={clientPhone}
          payment={invoicePayment as any}
        />
      )}
    </div>
  );
}
